import React, { useState, useEffect } from 'react';
import {
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Box
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import { HighlightOff, SettingsBackupRestore } from '@material-ui/icons';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import { Pagination } from '@material-ui/lab';
import { api } from '../../services/api';
import { maskCNPJ, maskCPF } from '../../utils/masks';
import { Button } from '@components/Button';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { ContainerButtonsTop, DivSearch } from './styles';
import { NavLink } from '@components/NavLink';
import usePagination from '@hooks/usePagination'; // Reintroduzido para paginação no cliente

// Interface
export interface ICredenciado {
  id_clinicas_credenciadas: number;
  contrato: string;
  nome_comercial: string;
  cpf_cnpj: string;
  dias_funcionamento: string;
  cep: string;
  rua: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  nome_med: string;
  crm: string;
  crm_estado: string;
  trabalho_agendamento: string;
  pagamento_adiantado: string;
  email_agendamento: string;
  email_financeiro: string;
  email_comercial: string;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  }
);

const PER_PAGE = 30;

export const Credenciados = () => {
  // Estados para a nova lógica
  const [loading, setLoading] = useState(true); // Começa como true
  const [credenciados, setCredenciados] = useState<ICredenciado[]>([]); // Guarda TODOS os credenciados
  const [searchValue, setSearchValue] = useState('');
  const [dialogDelete, setDialogDelete] = useState(false);
  const [edit, setEdit] = useState<ICredenciado | null>(null);
  const [snack, setSnack] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<
    'success' | 'error' | 'info' | 'warning'
  >('success');
  const [page, setPage] = useState(1);

  // 1. Lógica de filtro e paginação no cliente
  const filteredData = credenciados.filter(
    (credenciado) =>
      credenciado.nome_comercial.toLowerCase().includes(searchValue.toLowerCase()) ||
      credenciado.cpf_cnpj.includes(searchValue)
  );

  const count = Math.ceil(filteredData.length / PER_PAGE);
  const _DATA = usePagination(filteredData, PER_PAGE);
  const currentData = _DATA.currentData();

  // 2. Função para buscar TODOS os dados de uma vez
  const dataCredenciado = async () => {
    try {
      setLoading(true);
      // A chamada de API agora NÃO envia 'params'
      const res = await api.get(`ravim/clinicas`);
      setCredenciados(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar credenciados:', error);
      setSnack(true);
      setMessage('Erro ao carregar dados. Tente novamente.');
      setSeverity('error');
      setCredenciados([]);
    } finally {
      setLoading(false);
    }
  };

  // 3. Efeito para buscar os dados apenas uma vez, quando o componente monta
  useEffect(() => {
    dataCredenciado();
  }, []);

  // 4. Manipuladores de eventos
  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
    _DATA.jump(newPage);
    window.scrollTo(0, 0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setPage(1); // Reseta a paginação para a primeira página ao buscar
    _DATA.jump(1);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setPage(1);
    _DATA.jump(1);
  };

  const closeDialog = () => {
    setDialogDelete(false);
    setEdit(null);
  };

  const deleteCredenciado = (credenciado: ICredenciado) => {
    setEdit(credenciado);
    setDialogDelete(true);
  };

  const confirmDelete = async () => {
    if (!edit) return;
    try {
      const res = await api.delete(`ravim/exclu-clinica`, {
        data: { num_cnpj_cpf: edit.cpf_cnpj }
      });
      setSnack(true);
      setMessage(res.data.message || 'Credenciado excluído com sucesso!');
      setSeverity('success');
      closeDialog();
      // Atualiza a lista localmente sem precisar de uma nova chamada de API
      setCredenciados(prev => prev.filter(c => c.id_clinicas_credenciadas !== edit.id_clinicas_credenciadas));
    } catch (err: any) {
      setSnack(true);
      setMessage(err.response?.data?.message || 'Erro ao excluir credenciado.');
      setSeverity('error');
    }
  };

  const handleSnackClose = () => setSnack(false);

  const selectCpfOrCnpj = (cpfOrCnpj: string) => {
    if (cpfOrCnpj.length <= 11) {
      return maskCPF(cpfOrCnpj);
    }
    return maskCNPJ(cpfOrCnpj);
  };

  // 5. Renderização (JSX) com a lógica de loading correta
  return (
    <Container>
      <ContainerButtonsTop>
        <DivSearch>
          <TextField
            label="Pesquisar por Nome ou CPF/CNPJ"
            type="text"
            variant="outlined"
            value={searchValue}
            onChange={handleSearchChange}
            fullWidth
          />
          {searchValue && (
            <Button
              variantstyle="RED"
              icon={<X size={20} />}
              onClick={handleClearSearch}
              title="Limpar filtro"
            />
          )}
        </DivSearch>
        <NavLink to="/cadastro-credenciados">
          <Button title="Cadastro Credenciado" />
        </NavLink>
      </ContainerButtonsTop>

      <Typography variant="h4" align="center" style={{ margin: '20px 0', fontWeight: 'bold' }}>
        Gerenciamento de Credenciados
      </Typography>

      <Typography variant="h6" align="left" style={{ marginBottom: 16 }}>
        Total de registros: {filteredData.length} 
      </Typography>

      {count > 1 && (
        <Box display="flex" justifyContent="center" marginBottom={2}>
          <Pagination
            count={count}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      <TableContainer component={Paper} elevation={3}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell><strong>CPF/CNPJ</strong></TableCell>
              <TableCell><strong>Nome Comercial</strong></TableCell>
              <TableCell><strong>Horário de Atendimento</strong></TableCell>
              <TableCell><strong>Cidade/Estado</strong></TableCell>
              <TableCell><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" style={{ padding: '40px 0' }}>
                  <CircularProgress size={40} />
                  <Typography variant="body1" style={{ marginTop: 16 }}>
                    Carregando Credenciados...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : currentData.length > 0 ? (
              currentData.map((credenciado) => (
                <TableRow key={credenciado.id_clinicas_credenciadas} hover>
                  <TableCell>{selectCpfOrCnpj(credenciado.cpf_cnpj)}</TableCell>
                  <TableCell>{credenciado.nome_comercial}</TableCell>
                  <TableCell>{credenciado.dias_funcionamento}</TableCell>
                  <TableCell>{`${credenciado.cidade}/${credenciado.estado}`}</TableCell>
                  <TableCell>
                    <Box display="flex">
                      <Link to={{ pathname: '/editar-credenciado', state: credenciado }}>
                        <IconButton title="Editar">
                          <SettingsBackupRestore color="primary" />
                        </IconButton>
                      </Link>
                      <IconButton title="Excluir" onClick={() => deleteCredenciado(credenciado)}>
                        <HighlightOff color="error" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" style={{ padding: '40px 0' }}>
                  <Typography variant="h6">
                    {searchValue
                      ? 'Nenhum credenciado encontrado com o filtro aplicado'
                      : 'Nenhum registro encontrado'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {count > 1 && (
        <Box display="flex" justifyContent="center" marginTop={3}>
          <Pagination
            count={count}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Dialogs e Snackbar (mantidos como estavam) */}
      <Dialog open={dialogDelete} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>Você está prestes a excluir o credenciado:</Typography>
          <Typography><strong>Nome:</strong> {edit?.nome_comercial || '-'}</Typography>
          <Typography><strong>CPF/CNPJ:</strong> {edit?.cpf_cnpj ? selectCpfOrCnpj(edit.cpf_cnpj) : '-'}</Typography>
          <Typography color="error" style={{ marginTop: 16, fontWeight: 'bold' }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} title="Cancelar" variantstyle="SECUNDARY" />
          <Button onClick={confirmDelete} title="Confirmar Exclusão" variantstyle="RED" autoFocus />
        </DialogActions>
      </Dialog>

      <Snackbar open={snack} autoHideDuration={6000} onClose={handleSnackClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleSnackClose} severity={severity}>
          {message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
